/**
 * The rest of the platform, named and described before it exists.
 *
 * The onboarding half of the spec is built; the marketplace half — portfolio
 * records, requirement posting, interest and messaging — is not. These pages
 * are honest about that and say what will be there, because a navigation item
 * that leads to a blank screen is worse than one that leads to a promise.
 */

export interface Destination {
  title: string;
  blurb: string;
  /** The concrete things this page will hold, from the product spec. */
  points: string[];
}

export const DESTINATIONS: Record<string, Destination> = {
  academy: {
    title: 'Academy',
    blurb: 'Courses, learning paths and assignments, ordered around the tracks you picked.',
    points: [
      'Learning paths and courses per track',
      'Assignments and challenges with deadlines',
      'Certificates on completion',
      'A student community and cohort boards'
    ]
  },
  projects: {
    title: 'Projects',
    blurb: 'Everything you are making, and everything you have finished.',
    points: [
      'Productions in progress, by stage',
      'Finished work, ready to promote to the portfolio',
      'Assignment work for Academy tracks'
    ]
  },
  portfolio: {
    title: 'Portfolio',
    blurb: 'The public record of your work — the thing companies actually look at.',
    points: [
      'Add a project: title, role, tools, year, credits',
      'YouTube and Vimeo links become embedded cards',
      'Projects · Images · Videos · Showreel',
      'A public profile companies can find and contact'
    ]
  },
  'find-projects': {
    title: 'Find Projects',
    blurb: 'Briefs posted by companies, filtered down to the ones worth your time.',
    points: [
      'Filter by category, budget, location, deadline and skills',
      'Project cards with company, brief, skills, budget and deadline',
      'Express interest with the portfolio pieces that fit',
      'Track interest through shortlist, discussion and hire'
    ]
  },
  'find-talent': {
    title: 'Find Talent',
    blurb: 'Search the crew by craft, tool and availability.',
    points: [
      'Filter by specialization, AI tools, industry and language',
      'Review portfolios and showreels in place',
      'Shortlist, message and hire',
      'Transparent talent signals — no mystery score'
    ]
  },
  requirements: {
    title: 'My Requirements',
    blurb: 'Post a brief in five steps and watch it move through the pipeline.',
    points: [
      'What you need · brief · references · deliverables',
      'Style, duration, deadline and production detail',
      'Budget — fixed, a range, or open to discussion',
      'Skills required, then publish',
      'Draft → Published → Interest → Shortlisted → Discussion → Hired → In production → Review → Completed'
    ]
  }
};
